import type { AssessmentScale } from '../../../types/assessment';

const opts = [
  { value: 0, label: "Jamais", subtitle: "Ça ne m'arrive pas" },
  { value: 1, label: "Rarement", subtitle: "Quelques fois par an" },
  { value: 2, label: "Parfois", subtitle: "De temps en temps" },
  { value: 3, label: "Souvent", subtitle: "Régulièrement" },
];

export const TSI_BASE: AssessmentScale = {
  id: 'tsi_base',
  code: 'V6',
  name: "Traumatismes Sexuels et Détresse Associée",
  shortName: "Trauma Sexuel",
  category: 'sexual_health',
  description: "Évalue les symptômes de détresse liés à des traumatismes sexuels ou des abus. Basé sur les travaux publiés de Briere (1995).",
  instructions: "Ces questions portent sur des expériences sexuelles difficiles et leurs traces. Ce n'est pas un sujet facile — prends ton temps et sache que tu es en sécurité ici",
  timeEstimateMinutes: 7,
  reference: "Briere, J. (1995). Trauma Symptom Inventory professional manual. Psychological Assessment Resources. Questions basées sur les articles publiés, pas l'outil commercial PAR Inc.",
  licenseNote: "Basé sur les construits théoriques publiés de Briere (1995). Ne constitue pas l'outil TSI-2 commercial.",
  warningMessage: "Ces questions portent sur des expériences sexuelles difficiles. Un score élevé ne veut pas dire que tu es « {{cassé|cassée}} » — il reflète l'impact d'un événement, pas ta valeur.",
  scoreRange: { min: 0, max: 48 },
  items: [
    { id: 1,  text: "Des pensées ou images à contenu sexuel te viennent en tête sans que tu le veuilles ?",                                     type: 'frequency', options: opts, subscale: 'intrusive' },
    { id: 2,  text: "Tu fais des rêves ou cauchemars à contenu sexuel qui te perturbent ?",                                                     type: 'frequency', options: opts, subscale: 'intrusive' },
    { id: 3,  text: "Tu revois soudainement des scènes d'expériences sexuelles difficiles — comme si tu y étais encore ?",                      type: 'frequency', options: opts, subscale: 'intrusive' },
    { id: 4,  text: "Tu évites certaines personnes, certains endroits ou situations qui te rappellent une expérience sexuelle difficile ?",      type: 'frequency', options: opts, subscale: 'avoidance' },
    { id: 5,  text: "Tu as perdu l'envie ou l'intérêt pour la sexualité à cause d'expériences passées difficiles ?",                            type: 'frequency', options: opts, subscale: 'avoidance' },
    { id: 6,  text: "Tu préfères ne pas parler ni même penser à certaines expériences sexuelles difficiles ?",                                  type: 'frequency', options: opts, subscale: 'avoidance' },
    { id: 7,  text: "Pendant un rapport sexuel, tu te sens parfois {{détaché|détachée}} — comme si tu n'étais pas vraiment là ?",                         type: 'frequency', options: opts, subscale: 'dissociation' },
    { id: 8,  text: "Pendant un rapport, tu as l'impression de regarder ton corps de l'extérieur, comme si ce n'était pas toi ?",               type: 'frequency', options: opts, subscale: 'dissociation' },
    { id: 9,  text: "Il t'arrive de perdre le fil ou de ne pas te souvenir de ce qui s'est passé pendant une activité sexuelle ?",              type: 'frequency', options: opts, subscale: 'dissociation' },
    { id: 10, text: "Tu ressens de la honte ou de la culpabilité en lien avec une expérience sexuelle que tu as vécue ?",                       type: 'frequency', options: opts, subscale: 'shame' },
    { id: 11, text: "Il t'arrive de penser que ce qui t'est arrivé sexuellement est de ta faute ?",                                             type: 'frequency', options: opts, subscale: 'shame' },
    { id: 12, text: "Tu as du mal à parler de tes expériences sexuelles difficiles — par peur d'être {{jugé|jugée}} ou {{blâmé|blâmée}} ?",                   type: 'frequency', options: opts, subscale: 'shame' },
    { id: 13, text: "Quand tu repenses à une expérience sexuelle difficile, tu ressens une détresse émotionnelle forte ?",                      type: 'frequency', options: opts, subscale: 'distress' },
    { id: 14, text: "Ton corps réagit (palpitations, sueurs, tension) quand tu repenses à une expérience sexuelle difficile ?",                 type: 'frequency', options: opts, subscale: 'distress' },
    { id: 15, text: "Il t'arrive d'avoir des comportements sexuels à risque pour oublier ou fuir des souvenirs difficiles ?",                   type: 'frequency', options: opts, subscale: 'distress' },
    { id: 16, text: "Ces expériences sexuelles difficiles affectent tes relations ou ta vie de tous les jours ?",                                type: 'frequency', options: opts, subscale: 'distress' },
  ],
  subscales: [
    { key: 'intrusive',    label: "Pensées intrusives",   itemIds: [1,2,3],      range: { min: 0, max: 9 } },
    { key: 'avoidance',    label: "Évitement",            itemIds: [4,5,6],      range: { min: 0, max: 9 } },
    { key: 'dissociation', label: "Dissociation",         itemIds: [7,8,9],      range: { min: 0, max: 9 } },
    { key: 'shame',        label: "Honte et culpabilité", itemIds: [10,11,12],   range: { min: 0, max: 9 } },
    { key: 'distress',     label: "Détresse sexuelle",    itemIds: [13,14,15,16], range: { min: 0, max: 12 } },
  ],
  interpretation: [
    {
      min: 0, max: 15,
      label: "Peu de symptômes traumatiques sexuels",
      severity: 'minimal' as const,
      description: "Peu de symptômes liés à des expériences sexuelles difficiles. C'est rassurant.",
      referralRequired: false,
      recommendation: "Continue à maintenir un espace de dialogue sécurisant sur ta sexualité.",
    },
    {
      min: 16, max: 30,
      label: "Symptômes traumatiques sexuels modérés — un accompagnement peut t'aider",
      severity: 'moderate' as const,
      description: "Ton score montre que des expériences sexuelles difficiles laissent encore des traces dans ta vie. Ce n'est pas anodin, et tu mérites d'être {{entendu|entendue}}.",
      referralRequired: false,
      recommendation: "Un accompagnement par un professionnel spécialisé en trauma peut t'aider à avancer en douceur. Tu n'as pas à traverser ça {{seul|seule}}.",
    },
    {
      min: 31, max: 48,
      label: "Impact traumatique important — tu mérites du soutien",
      severity: 'severe' as const,
      description: "Les traces de cette expérience sont encore très présentes et pèsent sur ta vie. Tu n'as pas à porter ça {{seul|seule}} — des professionnels spécialisés peuvent t'aider à avancer.",
      referralRequired: true,
      recommendation: "Consulter un psychologue ou thérapeute spécialisé en trauma sexuel est fortement recommandé. C'est un acte de courage, pas de faiblesse.",
    },
  ],

  contextQuestion: {
    id: 17,
    text: "Les expériences sexuelles difficiles que tu as vécues, c'est quelque chose qui... ?",
    options: [
      { value: 1, label: "Appartient au passé — j'ai fait un vrai travail de guérison", subtitle: "C'est derrière moi" },
      { value: 2, label: "Est plutôt derrière moi, même si ça me touche encore un peu", subtitle: "J'ai avancé" },
      { value: 3, label: "M'affecte encore régulièrement dans ma vie", subtitle: "Ça pèse sur moi" },
      { value: 4, label: "Est récent ou toujours très présent et douloureux", subtitle: "C'est encore très vif" },
    ],
    noScore: true as const,
    resolvedThreshold: 2,
  },

  resolvedInterpretation: [
    {
      min: 0, max: 15,
      label: "Peu de traces traumatiques",
      severity: 'minimal' as const,
      description: "Peu de symptômes liés à des expériences sexuelles difficiles.",
      referralRequired: false,
      recommendation: "Continue à maintenir un espace de dialogue sécurisant sur ta sexualité.",
    },
    {
      min: 16, max: 30,
      label: "Empreinte d'une expérience difficile — chemin parcouru",
      severity: 'mild' as const,
      description: "Il reste quelques traces d'une expérience sexuelle difficile, mais tu as visiblement avancé dans ta guérison. C'est un signe de force.",
      referralRequired: false,
      recommendation: "Si ces traces te gênent encore parfois, un professionnel spécialisé peut t'aider à les apaiser davantage. Ce n'est pas une urgence — c'est un choix pour toi.",
    },
    {
      min: 31, max: 48,
      label: "Impact encore présent malgré le temps",
      severity: 'moderate' as const,
      alertLevel: 1,
      description: "Même si tu as avancé, ton score montre que les traces de cette expérience sont encore significatives. Le temps seul ne suffit pas toujours.",
      referralRequired: false,
      recommendation: "Un accompagnement spécialisé en trauma sexuel peut faire une vraie différence, même des années après. Tu mérites ce soutien.",
    },
  ],
};
